import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL } from '../../config';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';
import '../Shared/Modal.css';

const Sidebar = ({ active }) => {
    const { t } = useTranslation(['navigation', 'common']);
    const navigate = useNavigate();

    const location = useLocation();
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [helpForm, setHelpForm] = useState({ subject: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
    const [isCollapsed, setIsCollapsed] = useState(() => {
        // On mobile, always start collapsed so dashboard content is visible
        if (window.innerWidth <= 992) return true;
        return localStorage.getItem('sidebarCollapsed') === 'true';
    });

    const toggleCollapse = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        localStorage.setItem('sidebarCollapsed', newState);
    };

    // Listen for the 'toggleSidebar' custom event dispatched by Header's ☰ button
    useEffect(() => {
        window.addEventListener('toggleSidebar', toggleCollapse);
        return () => window.removeEventListener('toggleSidebar', toggleCollapse);
    }, [isCollapsed]);

    const handleLogout = () => {
        // Clear all user session data
        localStorage.removeItem('userId');
        localStorage.removeItem('studentName');
        localStorage.removeItem('studentClass');
        localStorage.removeItem('boardId');
        localStorage.removeItem('classId');
        localStorage.removeItem('boardName');
        navigate('/login');
    };

    const handleHelpSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const userId = localStorage.getItem('userId');
            const res = await fetch(`${API_BASE_URL}submit_help_request.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    user_id: userId,
                    subject: helpForm.subject,
                    message: helpForm.message
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setSubmitStatus('success');
                setHelpForm({ subject: '', message: '' });
                setTimeout(() => {
                    setShowHelpModal(false);
                    setSubmitStatus(null);
                }, 2000);
            } else {
                setSubmitStatus('error');
            }
        } catch (err) {
            console.error("Help request error:", err);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Auto-determine active state if not explicitly passed
    const currentActive = active || (location.pathname.includes('dashboard') ? 'dashboard' :
        location.pathname.includes('analysis') ? 'analysis' :
            location.pathname.includes('subscriptions') ? 'subscription' : 'tests');

    const menuItems = [
        {
            key: 'dashboard', label: t('navigation:sidebar.dashboard', 'Dashboard'), path: '/dashboard',
            icon: (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M4 13h6c1.1 0 2 .9 2 2v4c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2zM2 6c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v4c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6zm12 5h6c1.1 0 2 .9 2 2v4c0 1.1-.9 2-2 2h-6c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2zM14 4h6c1.1 0 2 .9 2 2v2c0 1.1-.9 2-2 2h-6c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                </svg>
            ),
        },
        {
            key: 'subjects',
            label: t('navigation:sidebar.subjects', 'Subjects'),
            path: '/subjects',
            icon: <img src="/student.svg" alt="Subjects" className="nav-icon-img" />
        },
        { key: 'subscription_plans', label: t('navigation:sidebar.subscription', 'Subscription'), path: '/pricing', icon: '💳' },
        { key: 'model_paper', label: t('navigation:sidebar.modelPaper', 'Model Paper'), path: '/generate-paper', icon: '📝' },
        { key: 'exam_booster', label: t('navigation:sidebar.examBooster', 'Exam Booster'), path: '/exam-booster', icon: '🚀' },
        { key: 'analysis', label: t('navigation:sidebar.analysis', 'Analysis'), path: '/analysis', icon: '📈' },
        { key: 'subscription', label: t('navigation:sidebar.myOrders', 'My Orders'), path: '/subscriptions', icon: '🧾' },
        { key: 'profile', label: t('navigation:sidebar.profile', 'Profile'), path: '/profile', icon: '👤' },
    ];


    // Standard function to close sidebar if in mobile/tablet view
    const closeSidebarOnMobile = () => {
        if (window.innerWidth <= 992) {
            setIsCollapsed(true);
            localStorage.setItem('sidebarCollapsed', 'true');
        }
    };

    return (
        <>
            <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-brand">
                    <img src="/app_ic.png" alt="SkillTherapy AI" className="brand-icon" />
                    {!isCollapsed && (
                        <div>
                            <p className="brand-title">SkillTherapy AI</p>
                            <p className="brand-subtitle">Student Portal</p>
                        </div>
                    )}
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map(item => (
                        <button
                            key={item.key}
                            className={`nav-link ${currentActive === item.key ? 'active' : ''}`}
                            onClick={() => {
                                navigate(item.path);
                                closeSidebarOnMobile();
                            }}
                            title={isCollapsed ? item.label : ''}
                        >
                            <i className="nav-icon">{item.icon}</i>
                            {!isCollapsed && <span>{item.label}</span>}
                        </button>
                    ))}

                    {/* Logout — always at the bottom of nav */}
                    <button className="nav-link nav-logout" onClick={() => {
                        setShowLogoutModal(true);
                        closeSidebarOnMobile();
                    }} title={isCollapsed ? t('navigation:sidebar.logout', 'Log Out') : ''}>
                        <i className="nav-icon">🚪</i>
                        {!isCollapsed && <span>{t('navigation:sidebar.logout', 'Log Out')}</span>}
                    </button>

                </nav>

                {!isCollapsed && (
                    <div className="sidebar-footer">
                        <p className="help-title">{t('navigation:sidebar.needHelp', 'Need help?')}</p>
                        <p className="help-text">{t('navigation:sidebar.contactAdvisor', 'Contact your advisor or visit the help center.')}</p>
                        <button className="support-btn" type="button" onClick={() => {
                            setShowHelpModal(true);
                            closeSidebarOnMobile();
                        }}>{t('navigation:sidebar.getSupport', 'Get Support')}</button>
                    </div>
                )}

            </aside>

            {/* Mobile Backdrop */}
            {!isCollapsed && (
                <div className="sidebar-mobile-backdrop" onClick={() => setIsCollapsed(true)}></div>
            )}

            {/* Portal: renders into document.body to escape sidebar's sticky stacking context */}
            {showLogoutModal && ReactDOM.createPortal(
                <div className="logout-overlay" onClick={() => setShowLogoutModal(false)}>
                    <div className="logout-modal" onClick={e => e.stopPropagation()}>
                        <div className="logout-modal-icon">🚪</div>
                        <h3>{t('navigation:sidebar.logoutConfirmTitle', 'Log Out?')}</h3>
                        <p>{t('navigation:sidebar.logoutConfirmText', 'Are you sure you want to log out of SkillTherapy AI?')}</p>
                        <div className="logout-modal-actions">
                            <button className="logout-cancel-btn" onClick={() => setShowLogoutModal(false)}>
                                {t('common:cancel', 'Cancel')}
                            </button>
                            <button className="logout-confirm-btn" onClick={handleLogout}>
                                {t('navigation:sidebar.yesLogout', 'Yes, Log Out')}
                            </button>
                        </div>
                    </div>

                </div>,
                document.body
            )}
            {/* Help Support Modal */}
            {showHelpModal && ReactDOM.createPortal(
                <div className="modal-overlay" onClick={() => setShowHelpModal(false)}>
                    <div className="help-modal" onClick={e => e.stopPropagation()}>
                        <div className="help-modal-header">
                            <div className="help-icon-box">✉️</div>
                            <h3>{t('navigation:sidebar.helpModal.title', 'How can we help?')}</h3>
                            <p>{t('navigation:sidebar.helpModal.subtitle', "Send us a message and we'll get back to you soon.")}</p>
                        </div>

                        <form onSubmit={handleHelpSubmit} className="help-form">
                            <div className="form-group">
                                <label htmlFor="help-subject">{t('navigation:sidebar.helpModal.labelSubject', 'Subject')}</label>
                                <input
                                    id="help-subject"
                                    name="subject"
                                    type="text"
                                    required
                                    placeholder={t('navigation:sidebar.helpModal.placeholderSubject', 'Briefly describe the issue')}
                                    value={helpForm.subject}
                                    onChange={e => setHelpForm({ ...helpForm, subject: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="help-message">{t('navigation:sidebar.helpModal.labelMessage', 'Message')}</label>
                                <textarea
                                    id="help-message"
                                    name="message"
                                    required
                                    placeholder={t('navigation:sidebar.helpModal.placeholderMessage', 'Write your query here...')}
                                    value={helpForm.message}
                                    onChange={e => setHelpForm({ ...helpForm, message: e.target.value })}
                                ></textarea>
                            </div>

                            {submitStatus === 'success' && <p className="status-msg success">✅ {t('navigation:sidebar.helpModal.success', 'Request sent successfully!')}</p>}
                            {submitStatus === 'error' && <p className="status-msg error">❌ {t('navigation:sidebar.helpModal.error', 'Something went wrong. Try again.')}</p>}

                            <div className="help-modal-actions">
                                <button type="button" className="help-cancel-btn" onClick={() => setShowHelpModal(false)}>
                                    {t('common:cancel', 'Cancel')}
                                </button>
                                <button type="submit" className="help-submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? t('navigation:sidebar.helpModal.sending', 'Sending...') : t('navigation:sidebar.helpModal.sendMessage', 'Send Message')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default Sidebar;
